import "../Css/contact.css";
import React, { useState, useMemo, useEffect, useRef } from "react";

const COLUMNS = 3;
const columnPairs = [
  [0, 2],
  [1, 2],
  [0, 1],
];

// Hoisted out of the component so it's the same array reference across
// every render — as a literal declared inside WorkedWith(), this would be a
// brand-new array every render, which silently broke the `useMemo`s below
// (their `[logos]` dependency never actually stayed equal) and made the
// desktop draw-in animation replay every time a logo was clicked.
const logos = [
  {
      src: "https://ik.imagekit.io/harshitsharma/company_logos/flymeout_kCKiRKaAIe.svg",
      name: "FlyMeOut",
      alt: "FlyMeOut logo",
      tag: true,
      testimonial: {
        heading:
          "FlyMeOut connects travelers into exclusive communities. Harshit made our group coordination interface so intuitive that users are now defaulting to the application to organize their next trips.",
        personName: "Zach Latos",
        personRole: "CO-FOUNDER · FLYMEOUT",
      },
    },
    {
      name: "Change Wealth",
      src: "https://ik.imagekit.io/harshitsharma/company_logos/changewealth_FbEhjtZjNG.svg",
      alt: "Change Wealth logo",
      tag: true,
      testimonial: {
        heading:
         "Harshit did a great job on the Change Wealth portfolio interface, improving the user experience. His ability to work closely with designers and developers helped us deliver the updates to our users promptly and smoothly.",
        personName: "Param Agrawal",
        personRole: "CO-FOUNDER · CHANGE WEALTH",
      },
    },
    {
      name: "Workday",
      src: "https://ik.imagekit.io/harshitsharma/company_logos/workday_G_jAz7d-A.svg",
      alt: "Workday icon",
      testimonial: {
        heading:
          "Built and mentored a cross-functional design team from scratch",
        personName: "Internal Team",
        personRole: "PRODUCT & DESIGN",
      },
    },
    {
      name: "Optty",
      src: "https://ik.imagekit.io/harshitsharma/company_logos/optty_roRLorI2y.svg",
      alt: "Optty logo",
      tag: true,
      testimonial: {
        heading:
          "Payment orchestration used to be complicated. Harshit built our merchant dashboard to make the more than 145 payment methods we support feel effortless to integrate. Substantially reduced friction for our customers.",
        personName: "Steve Ritchie",
        personRole: "CTO · OPTTY",
      },
    },
    {
      name: "Lenskart",
      src: "https://ik.imagekit.io/harshitsharma/company_logos/lenskart_khVC2a-YL.svg",
      alt: "Lenskart logo",
      tag: true,
      testimonial: {
        heading:
          "Harshit engineered a PLP Conversion for Lenskart that let users narrow from 5,000+ frames to their match in seconds. His ability to bridge complexity with elegant UX stood out.",
        personName: "Samarth Gupta",
        personRole: "PRODUCT MANAGER · LENSKART",
      },
    },
    {
      name: "Greenfi",
      src: "https://ik.imagekit.io/harshitsharma/company_logos/greenfi_i_MwSvOl1.svg",
      alt: "Greenfi logo",
         tag: true,
      testimonial: {
        heading:
          "Harshit designed ESG AI investment dashboards that made climate impact clear and actionable, combining his finance expertise with strong React skills.",
        personName: "Barun Chandran",
        personRole: "FOUNDER · GREENFI",
      },
    },
    {
      name: "Instantpay",
      src: "https://ik.imagekit.io/harshitsharma/company_logos/instantpay_YrnknjcdO.svg",
      alt: "Instantpay logo",
      tag: true,
      testimonial: {
        heading: "Harshit redesigned Instantpay's onboarding for underbanked users, increasing first-time completion by 35%. His fintech + design expertise is rare.",
        personName: "Jyoti Swaroop Mohanty",
        personRole: "DIRECTOR OF PRODUCT · INSTANTPAY",
      },
    },
    {
      name: "Hcode",
      src: "https://ik.imagekit.io/harshitsharma/company_logos/hcode_IsRN1tXYGh.svg",
      alt: "Hcode logo",
      testimonial: {
        heading: "Crafted a developer-first brand identity and marketing site",
        personName: "Rohan Das",
        personRole: "CO-FOUNDER · HCODE",
      },
    },
    {
      name: "Lejit",
      src: "https://ik.imagekit.io/harshitsharma/company_logos/lejit_4LfinTr2N.svg",
      alt: "Lejit logo",
      tag: true,
      testimonial: {
        heading:
          "Lejit.ai's legal interface needed to simplify workflows for both lawyers and everyday users. Harshit cut form completion time in half with intuitive design.",
        personName: "Barun Chandran",
        personRole: "FOUNDER · LEJIT",
      },
    },
  ];

function WorkedWith() {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = logos[activeIndex].testimonial;

  const [activeModal, setActiveModal] = useState(null);

  // 2 logos per row → rows = ceil(logos.length / 2)
  const rows = Math.ceil(logos.length / 2);
  const totalCells = rows * COLUMNS;

  const gridCells = useMemo(() => {
    const cells = Array.from({ length: totalCells }, () => null);
    let logoIndex = 0;

    for (let row = 0; row < rows && logoIndex < logos.length; row++) {
      const [colA, colB] = columnPairs[row % columnPairs.length];

      cells[row * COLUMNS + colA] = logos[logoIndex];
      logoIndex++;

      if (logoIndex < logos.length) {
        cells[row * COLUMNS + colB] = logos[logoIndex];
        logoIndex++;
      }
    }

    return cells;
  }, [logos, rows, totalCells]);

  // Each desktop logo cell draws in from a random edge, computed once so it doesn't reshuffle on re-render.
  const logoCellDirections = useMemo(() => {
    const directions = ["draw-left", "draw-right", "draw-top", "draw-bottom"];
    return logos.map(
      () => directions[Math.floor(Math.random() * directions.length)],
    );
  }, [logos]);

  // Plays the logo grid draw-in animation once, the first time it scrolls into view.
  const logosGridRef = useRef(null);
  const [logosGridInView, setLogosGridInView] = useState(false);

  useEffect(() => {
    if (!logosGridRef.current || logosGridInView) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLogosGridInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    observer.observe(logosGridRef.current);
    return () => observer.disconnect();
  }, [logosGridInView]);

  useEffect(() => {
    if (activeModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    // cleanup: always restore scroll if component unmounts while modal is open
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeModal]);

  return (
    <section className="worked-with-section">
      <span className="worked-with-tag">PROUD TO HAVE WORKED WITH →</span>

      {/* ---------- DESKTOP (unchanged) ---------- */}
      <div className="worked-with-grid desktop-only">
        <div className="testimonial-card">
          <h2 className="testimonial-heading">{active.heading}</h2>

          <div className="testimonial-person">
            <div
              className="person-avatar-placeholder"
              role="img"
              aria-label={`${active.personName} avatar`}
            ></div>
            <div className="person-info">
              <span className="person-name">{active.personName}</span>
              <span className="person-role">{active.personRole}</span>
            </div>
          </div>
        </div>

        <div
          className={`logos-grid ${logosGridInView ? "in-view" : ""}`}
          ref={logosGridRef}
        >
          {logos.map((logo, i) => {
            const cellContent = (
              <>
                <img src={logo.src} alt={logo.alt} />
                {logo.tag && (
                  <span className="cell-asterisk">
                    {" "}
                    <img
                      className="star-icon"
                      style={{ height: "20px", width: "20px" }}
                      src="https://ik.imagekit.io/harshitsharma/Thumbnails_and_Logo/star_mICqJYH8o.svg"
                      alt=""
                    />
                  </span>
                )}
                {logo.label && (
                  <span className="logo-label">{logo.label}</span>
                )}
              </>
            );

            const drawClass = logoCellDirections[i];
            const cellDelay = { animationDelay: `${i * 0.1
              
            }s` };

            // Only logos flagged with `tag` are selectable; the rest are static.
            return logo.tag ? (
              <button
                type="button"
                className={`logo-cell ${drawClass} ${logo.highlight ? "highlight" : ""} ${
                  i === activeIndex ? "active" : ""
                }`}
                key={i}
                style={cellDelay}
                onClick={() => setActiveIndex(i)}
              >
                {cellContent}
              </button>
            ) : (
              <div
                className={`logo-cell ${drawClass} not-clickable ${logo.highlight ? "highlight" : ""}`}
                key={i}
                style={cellDelay}
              >
                {cellContent}
              </div>
            );
          })}

          <div className="many-more-row">+ MANY MORE</div>
        </div>
      </div>

      {/* ---------- MOBILE: random grid + modal ---------- */}
      <div className="random-logo-grid mobile-only">
        {gridCells.map((logo, i) => {
          if (!logo) {
            return <div className="random-grid-cell empty" key={i}></div>;
          }

          // Match desktop: only tagged logos are clickable/open a modal.
          return logo.tag ? (
            <button
              type="button"
              className="random-grid-cell filled"
              key={i}
              onClick={() => setActiveModal(logo)}
            >
              <img src={logo.src} alt={logo.alt} className="random-grid-logo" />
            </button>
          ) : (
            <div className="random-grid-cell filled not-clickable" key={i}>
              <img src={logo.src} alt={logo.alt} className="random-grid-logo" />
            </div>
          );
        })}
      </div>

      {activeModal && (
        <div
          className="testimonial-modal-overlay mobile-only"
          onClick={() => setActiveModal(null)}
        >
          <div className="modal-wrapper">
            <button
              type="button"
              className="modal-close-btn-outside"
              onClick={() => setActiveModal(null)}
            >
              ✕
            </button>

            <div
              className="testimonial-modal-card testimonial-card"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="testimonial-heading">
                {activeModal.testimonial.heading}
              </h2>

              <div className="testimonial-person">
                <div
                  className="person-avatar-placeholder"
                  role="img"
                  aria-label={`${activeModal.testimonial.personName} avatar`}
                ></div>
                <div className="person-info">
                  <span className="person-name">
                    {activeModal.testimonial.personName}
                  </span>
                  <span className="person-role">
                    {activeModal.testimonial.personRole}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default WorkedWith;
